import {
  Button,
  ButtonVariant,
  IconIdentifier,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import FileSaver from 'file-saver';
import React, { useEffect, useRef, useState } from 'react';

type BackupCodesProps = {
  codes: string[];
  className?: string;
};

export const BackupCodes: React.FC<BackupCodesProps> = ({
  codes,
  className,
}) => {
  // States.
  const [isCopied, setIsCopied] = useState(false);

  // Refs.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Effects.
  useEffect(() => {
    return () => {
      clearTimeoutRef();
    };
  }, []);

  // Handlers.
  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(formattedCodes);

    setIsCopied(true);
    clearTimeoutRef();

    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, 500);
  };

  const handleDownload = () => {
    const blob = new Blob([formattedCodes], {
      type: 'text/plain;charset=utf-8',
    });

    FileSaver.saveAs(blob, 'backup_codes.txt');
  };

  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const formatBackupCode = (code: string): string => {
    const splitIndex = code.length / 2;

    return `${code.slice(0, splitIndex)}-${code.slice(splitIndex)}`;
  };

  // Variables.
  const customClassname = classNames('backup-codes', className);
  const formattedCodes = codes
    .map((code: string) => formatBackupCode(code))
    .join('\n');

  return (
    <div className={customClassname}>
      <div className="backup-codes__grid">
        {codes.map((code: string, index) => (
          <span key={`${code}-${index}`} className="backup-codes__item">
            {formatBackupCode(code)}
          </span>
        ))}
      </div>

      <div className="backup-codes__actions">
        <Button
          variant={ButtonVariant.Outline}
          leftIconIdentifier={
            isCopied ? IconIdentifier.CheckCircle : IconIdentifier.Copy
          }
          onClick={handleCopyAll}
        >
          {isCopied ? 'Copied' : 'Copy All'}
        </Button>

        <Button
          variant={ButtonVariant.Outline}
          leftIconIdentifier={IconIdentifier.Download}
          onClick={handleDownload}
        >
          Download
        </Button>
      </div>
    </div>
  );
};
